import { ChangeEvent, useCallback, useRef, useState } from 'react'
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { uploadCertDocument } from '../../services/storageService'

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.pdf'
const MIN_FILE_SIZE = 1024 // 1 KB
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

type UploadState = 'idle' | 'uploading' | 'complete' | 'error'

export interface CertDocumentUploadProps {
  userId: string
  onUploadComplete: (url: string) => void
  onUploadStart?: () => void
  onUploadError?: (error: Error) => void
  disabled?: boolean
}

const CertDocumentUpload = ({
  userId,
  onUploadComplete,
  onUploadStart,
  onUploadError,
  disabled = false
}: CertDocumentUploadProps) => {
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [progress, setProgress] = useState(0)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return `Invalid file type: "${file.type}". Accepted types: JPEG, PNG, PDF.`
    }
    if (file.size < MIN_FILE_SIZE) {
      return `File too small (${file.size} bytes). Minimum size is 1 KB.`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (${(file.size / (1024 * 1024)).toFixed(
        1
      )} MB). Maximum size is 10 MB.`
    }
    return null
  }

  const handleUpload = useCallback(
    async (file: File) => {
      setUploadState('uploading')
      setProgress(0)
      setUploadError(null)
      onUploadStart?.()

      try {
        const url = await uploadCertDocument(file, userId, percent => {
          setProgress(Math.round(percent))
        })
        setUploadState('complete')
        setProgress(100)
        onUploadComplete(url)
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Upload failed')
        setUploadState('error')
        setUploadError(error.message)
        onUploadError?.(error)
      }
    },
    [userId, onUploadComplete, onUploadStart, onUploadError]
  )

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setValidationError(null)
    setUploadError(null)
    setFileName(file.name)

    const error = validateFile(file)
    if (error) {
      setValidationError(error)
      setUploadState('idle')
      // Reset input so user can re-select
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    handleUpload(file)
  }

  const handleRetry = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setUploadState('idle')
    setUploadError(null)
    setProgress(0)
    fileInputRef.current?.click()
  }

  const isUploading = uploadState === 'uploading'

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant='subtitle2' gutterBottom>
        Certification Document
      </Typography>

      <input
        ref={fileInputRef}
        type='file'
        accept={ALLOWED_EXTENSIONS}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        hidden
        aria-label='Upload certification document'
      />

      {uploadState === 'idle' && (
        <Button
          variant='outlined'
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          Choose File
        </Button>
      )}

      {isUploading && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress variant='determinate' value={progress} />
            </Box>
            <Typography variant='body2' color='text.secondary'>
              {progress}%
            </Typography>
          </Box>
          {fileName && (
            <Typography variant='caption' color='text.secondary'>
              Uploading: {fileName}
            </Typography>
          )}
        </Box>
      )}

      {uploadState === 'complete' && (
        <Alert severity='success' sx={{ mt: 1 }}>
          {fileName} uploaded successfully
        </Alert>
      )}

      {uploadState === 'error' && (
        <Box sx={{ mt: 1 }}>
          <Alert severity='error' sx={{ mb: 1 }}>
            {uploadError}
          </Alert>
          <Button variant='outlined' size='small' onClick={handleRetry}>
            Retry Upload
          </Button>
        </Box>
      )}

      {validationError && (
        <Alert severity='error' sx={{ mt: 1 }}>
          {validationError}
        </Alert>
      )}
    </Box>
  )
}

export default CertDocumentUpload
